// utils
import { ALL_RECORDS_COUNT, apiConfig } from 'config';
import { BoostLookUpTypes, IBoostGroupHoursData, IImage } from '../@types/boost';
import { ImageTypes } from '../@types/boostGroup';
import { WrapperService } from './wrapper';

// ----------------------------------------------------------------------

export interface IBoostQueryParams extends IBoostParamsDevice {
  email?: string;
  ownerId?: any; // We need to check with backend info which is the correct type.
  boostGroupId?: any; // We need to check with backend info which is the correct type.
  subBoostGroupId?: number;
  boostId?: number;
  isSubGroup?: boolean;
  unassigned?: boolean;
  currentPage?: number;
  limit?: number;
  searchFilter?: string;
}

export interface IBoostParamsDevice {
  boostSerialNumber?: string;
  ownerName?: string;
  owner?: string;
  boostGroup?: any;
  boostGroupName?: string;
}

export class BoostService {
  public apiService: WrapperService = new WrapperService();

  public getChargePoints(params: {
    page?: number;
    size?: number;
    boostName?: string;
    boostGroup?: string;
    ownerId?: number;
  }) {
    const { page = 0, size = ALL_RECORDS_COUNT, boostName, boostGroup, ownerId = -1 } = params;
    const apiParam = {
      method: 'GET',
      baseURL: apiConfig.asset_mgt,
      params: { page, size, searchInput: boostName, boostGroup },
      url: `${ownerId}/chargerPoint/list`
    };
    return this.apiService.httpCall(apiParam);
  }

  public getBoostsList(params: IBoostQueryParams) {
    const {
      unassigned,
      currentPage = 0,
      limit = 2000,
      searchFilter,
      ownerId,
      boostGroupId
    } = params;
    const apiParam = {
      method: 'GET',
      url: unassigned
        ? `/owner/${ownerId}/boost/unassigned`
        : `/owner/${ownerId}/boostGroup/${boostGroupId}/boosts`,
      baseURL: apiConfig.asset_mgt,
      params: {
        ownerId,
        page: currentPage,
        size: limit,
        searchInput: searchFilter
      }
    };
    return this.apiService.httpCall(apiParam);
  }

  public getBoost(ownerId: string, boostId: string) {
    const apiParam = {
      method: 'GET',
      baseURL: apiConfig.asset_mgt,
      url: `/owner/${ownerId}/boost/${boostId}`
    };
    return this.apiService.httpCall(apiParam);
  }

  /**
   * Get the image of the related boostGroup
   * @param ownerId
   * @param boostGroupId
   * @param imageType depending on which images of that type.
   * @returns
   */
  public getImages(ownerId: string, boostGroupId: number, imageType: ImageTypes) {
    const apiParam = {
      method: 'GET',
      url: `/owner/${ownerId}/image/boostGroup/${boostGroupId}/${imageType}/all`,
      baseURL: apiConfig.asset_mgt
    };
    return this.apiService.httpCall(apiParam);
  }

  public getLogos(ownerId: string | null) {
    const apiParam = {
      method: 'GET',
      url: ownerId ? `/logos?owner=${ownerId}` : `/logos`
    };
    return this.apiService.httpCall(apiParam);
  }

  /**
   * Put the image of the related boostGroup
   * @param ownerId
   * @param boostGroupId
   * @param imageType depending on which images of that type.
   * @returns
   */
  public saveImages(ownerId: string, boostGroupId: number, imageType: string, file: File) {
    const formData = new FormData();
    formData.append('boostGroupId', boostGroupId.toString());
    formData.append('imageType', imageType);
    formData.append('image', file);
    const apiParam = {
      method: 'PUT',
      body: formData,
      url: `/owner/${ownerId}/image/upload`,
      baseURL: apiConfig.asset_mgt
    };
    return this.apiService.httpCall(apiParam);
  }

  public deleteImage(ownerId: string, imageId: number) {
    const apiParam = {
      method: 'DELETE',
      url: `owner/${ownerId}/image/${imageId}/delete`,
      baseURL: apiConfig.asset_mgt
    };
    return this.apiService.httpCall(apiParam);
  }

  public postOperationHours(
    method = 'PUT',
    isTimeUseSection: boolean | undefined,
    ownerId: string | undefined,
    boostGroupId: string | undefined,
    data: IBoostGroupHoursData,
    isSubGroup?: boolean
  ) {
    const pointer = isSubGroup ? 'subBoostGroups' : 'boostGroups';
    const url = isTimeUseSection
      ? `/owners/${ownerId}/${pointer}/${boostGroupId}/timeOfUse`
      : `/owners/${ownerId}/${pointer}/${boostGroupId}/operationHours`;
    // Observable<any> Any because we need to set the common interface that the backend its going to return
    const apiParam = {
      method: method,
      body: data,
      url: url
    };
    return this.apiService.httpCall(apiParam);
  }

  public submitForm(method: string | undefined, body: any, url: string | undefined) {
    const apiParam = {
      method: method,
      url: url,
      body: body,
      baseURL: apiConfig.asset_mgt
    };
    return this.apiService.httpCall(apiParam);
  }

  public deleteImages(ownerId: string, imageId: number) {
    const apiParam = {
      method: 'DELETE',
      url: `/owner/${ownerId}/image/${imageId}/delete`,
      baseURL: apiConfig.asset_mgt
    };
    return this.apiService.httpCall(apiParam);
  }

  public lookup(library: BoostLookUpTypes) {
    const apiParam = {
      method: 'GET',
      url: `/lookup/${library}`,
      baseURL: apiConfig.asset
    };
    return this.apiService.httpCall(apiParam);
  }

  public deleteBoostDevice(ownerId: number, id: number) {
    const apiParam = {
      method: 'DELETE',
      baseURL: apiConfig.asset_mgt,
      url: `/owner/${ownerId}/boost/${id}/remove`
    };
    return this.apiService.httpCall(apiParam);
  }

  getImageUrlFromBuffer(buffer: any): string {
    const typedArr = new Uint8Array(buffer);
    const strChar = typedArr.reduce((data, byte) => {
      return data + String.fromCharCode(byte);
    }, '');
    return !strChar.includes('base64') ? `${'data:image/jpg;base64,'}${strChar}` : strChar;
  }

  /**
   * The images objects coming from te backend are not clear enough.
   * We need to build those objects to show the images properly.
   * @param images to build from the server to the frontend.
   * @param type of images to build.
   * @param isPReview if is preview get the opposite image to display in the preview example.
   */
  public getBuildImagesObjects(images: IImage[], type: ImageTypes, isPreview?: boolean) {
    if (isPreview && type === ImageTypes.GROUP_LOGO) {
      type = ImageTypes.GROUP_BACKGROUND;
    } else if (isPreview) {
      type = ImageTypes.GROUP_LOGO;
    }
    let filesStored = [] as any[]; // Building as any, we don't have an interface for this.
    images?.forEach((i: IImage) => {
      let preview = i?.imageUrl;
      const id = i.id;
      const name = i?.imageUrl?.split('/').pop();
      //some images do not have URLs, they are directly stored as binary data in the db
      if (!preview) {
        const buffer = i.image?.data;
        if (buffer) {
          preview = this.getImageUrlFromBuffer(buffer);
        }
      }
      const file = {
        id: id,
        name: name,
        lastModified: i.updatedAt,
        preview: preview,
        size: i.imageSize
      };
      filesStored = [...filesStored, file];
      // sorting the files to render the newly added images at the end.
      filesStored.sort((a, b) => (a.id > b.id ? 1 : -1));
    });
    return filesStored;
  }
}
